/**
 * Green Flag — BirthDateInput
 *
 * Segmented MM / DD / YYYY typed input for collecting a user's birth date.
 *
 * We deliberately avoid the native `<input type="date">` and calendar-popup
 * pickers here: a birth date is reliably decades in the past, and both of
 * those force the user to navigate back through years (the native mobile
 * wheel opens on the `max` date — today − minAge — and many users never
 * discover the year tap, perceiving the field as "stuck"). Typing into three
 * numeric segments is the fastest path and behaves identically across every
 * browser and device. `inputMode="numeric"` surfaces the mobile keypad.
 *
 * Validation:
 *   - dayjs parsing surfaces `valid` (a real calendar date) and `tooYoung`
 *     (parses but resolves under minAge). Callers gate Continue on both.
 *
 * Value contract is unchanged from the prior implementations: `value` and
 * `onChange` use the ISO `YYYY-MM-DD` shape. While the user is mid-entry the
 * three segments hold local state; `onChange` emits the ISO string once all
 * segments form a parseable date, and an empty string otherwise (so callers'
 * existing `validateBirthDate` gating keeps Continue disabled until complete).
 */

import { Flex, chakra } from "@chakra-ui/react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
} from "react";

dayjs.extend(customParseFormat);

export interface BirthDateInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Minimum age the validation will allow. Defaults to 18. */
  minAge?: number;
  autoFocus?: boolean;
  style?: CSSProperties;
}

export interface BirthDateValidation {
  /** Field is empty. */
  empty: boolean;
  /** Field has a parseable date. */
  valid: boolean;
  /** Parses but the resulting age is below `minAge`. */
  tooYoung: boolean;
}

/**
 * Validate a birth-date string against a minimum age. Exposed so callers can
 * drive their submit button's disabled state from the same logic the input
 * enforces. Uses strict parsing so partial/garbage input resolves to invalid.
 */
export function validateBirthDate(
  value: string,
  minAge = 18,
): BirthDateValidation {
  if (!value) return { empty: true, valid: false, tooYoung: false };
  const parsed = dayjs(value, "YYYY-MM-DD", true);
  if (!parsed.isValid()) {
    return { empty: false, valid: false, tooYoung: false };
  }
  const age = dayjs().diff(parsed, "year");
  return { empty: false, valid: true, tooYoung: age < minAge };
}

interface Segments {
  month: string;
  day: string;
  year: string;
}

const EMPTY: Segments = { month: "", day: "", year: "" };

/** Split an ISO `YYYY-MM-DD` value into segment strings. */
function isoToSegments(value: string): Segments {
  const parsed = dayjs(value, "YYYY-MM-DD", true);
  if (!value || !parsed.isValid()) return EMPTY;
  return {
    month: parsed.format("MM"),
    day: parsed.format("DD"),
    year: parsed.format("YYYY"),
  };
}

/** Join segments into an ISO string, or "" if they don't form a real date. */
function segmentsToIso({ month, day, year }: Segments): string {
  if (month.length === 0 || day.length === 0 || year.length !== 4) return "";
  const iso = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  return dayjs(iso, "YYYY-MM-DD", true).isValid() ? iso : "";
}

const segmentInputStyle = {
  bg: "transparent",
  border: "0",
  outline: "none",
  p: "0",
  h: "auto",
  fontFamily: "body",
  // 16px on mobile keeps iOS Safari from auto-zooming on focus; 15px desktop.
  fontSize: { base: "16px", md: "15px" },
  fontWeight: "medium",
  color: "fg.default",
  letterSpacing: "-0.005em",
  textAlign: "center" as const,
  minW: "0",
};

export function BirthDateInput({
  value,
  onChange,
  autoFocus,
  style,
}: BirthDateInputProps) {
  const [segments, setSegments] = useState<Segments>(() =>
    isoToSegments(value),
  );

  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  // Keep local segments in sync when the value is changed from the outside
  // (e.g. form reset / prefill). We only re-sync when the incoming ISO value
  // differs from what our own segments currently represent, so we never clobber
  // a partially-typed entry that hasn't yet formed a complete date.
  useEffect(() => {
    if (value !== segmentsToIso(segments)) {
      setSegments(isoToSegments(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = (next: Segments) => {
    setSegments(next);
    onChange(segmentsToIso(next));
  };

  const handleSegment =
    (key: keyof Segments, maxLen: number, next?: HTMLInputElement | null) =>
    (e: ChangeEvent<HTMLInputElement>) => {
      const digits = e.target.value.replace(/\D/g, "").slice(0, maxLen);
      commit({ ...segments, [key]: digits });
      if (digits.length === maxLen && next) next.focus();
    };

  const handleKeyDown =
    (key: keyof Segments, prev?: HTMLInputElement | null) =>
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && segments[key] === "" && prev) {
        prev.focus();
      }
    };

  // Focusing a segment that already has text selects its contents, so the
  // first keystroke replaces the value rather than appending to it.
  const handleSegmentFocus = (e: FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  // Clicking anywhere in the box (the wrapper padding or a `/` separator)
  // when nothing has been typed yet should land the cursor in the month
  // segment, rather than doing nothing. We only redirect when the press
  // target isn't already one of the segment inputs, so taps on the day/year
  // segments keep their natural placement.
  const handleWrapperMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    const isEmpty = !segments.month && !segments.day && !segments.year;
    if (isEmpty && e.target !== monthRef.current && monthRef.current) {
      e.preventDefault();
      monthRef.current.focus();
    }
  };

  return (
    <Flex
      align="center"
      w="100%"
      h="52px"
      px="14px"
      gap="2px"
      bg="bg.canvas"
      borderRadius="md"
      borderWidth="1.5px"
      borderColor="border.default"
      transition="border-color 120ms, box-shadow 120ms"
      _focusWithin={{
        borderColor: "border.brand",
        boxShadow: "0 0 0 4px {colors.bg.brandTint}",
      }}
      onMouseDown={handleWrapperMouseDown}
      style={style}
    >
      <chakra.input
        ref={monthRef}
        aria-label="Birth month"
        inputMode="numeric"
        placeholder="MM"
        value={segments.month}
        onChange={handleSegment("month", 2, dayRef.current)}
        onFocus={handleSegmentFocus}
        autoFocus={autoFocus}
        w="2.2em"
        {...segmentInputStyle}
      />
      <chakra.span color="fg.muted" userSelect="none">
        /
      </chakra.span>
      <chakra.input
        ref={dayRef}
        aria-label="Birth day"
        inputMode="numeric"
        placeholder="DD"
        value={segments.day}
        onChange={handleSegment("day", 2, yearRef.current)}
        onFocus={handleSegmentFocus}
        onKeyDown={handleKeyDown("day", monthRef.current)}
        w="2.2em"
        {...segmentInputStyle}
      />
      <chakra.span color="fg.muted" userSelect="none">
        /
      </chakra.span>
      <chakra.input
        ref={yearRef}
        aria-label="Birth year"
        inputMode="numeric"
        placeholder="YYYY"
        value={segments.year}
        onChange={handleSegment("year", 4)}
        onFocus={handleSegmentFocus}
        onKeyDown={handleKeyDown("year", dayRef.current)}
        w="3.6em"
        {...segmentInputStyle}
      />
    </Flex>
  );
}
